/**
 * The inspection badges: the board chip (with "late"), a visit's result, a car's condition and a
 * line's grade. Every one renders a code the server returned; none decides anything.
 */

import { cn } from '@/lib/utils';
import {
    ATTENDANCE_LABEL,
    CHIP_LABEL,
    CONDITION_LABEL,
    GRADE_LABEL,
    RESULT_LABEL,
    type Attendance,
    type BoardChip,
    type Condition,
    type Grade,
    type VisitResult,
} from '@/lib/inspections';

const TONE = {
    red: 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-400',
    amber: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-400',
    orange: 'border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-800 dark:bg-orange-950/40 dark:text-orange-400',
    green: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-400',
    blue: 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-400',
    slate: 'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-400',
} as const;

export type Tone = keyof typeof TONE;

const CHIP_TONE: Record<BoardChip, Tone> = {
    held: 'red',
    missed: 'orange',
    not_yet: 'blue',
    done: 'green',
    not_owed: 'slate',
};

const RESULT_TONE: Record<VisitResult, Tone> = {
    held: 'red',
    passed_fix_soon: 'amber',
    passed: 'green',
    notes_only: 'slate',
};

const ATTENDANCE_TONE: Record<Attendance, Tone> = {
    presented: 'green',
    not_yet: 'blue',
    held: 'red',
    missed: 'orange',
    not_owed: 'slate',
};

const CONDITION_TONE: Record<Condition, Tone> = { held: 'red', fix_soon: 'amber', clean: 'green' };

const GRADE_TONE: Record<Grade, Tone> = {
    fix_now: 'red',
    fix_soon: 'amber',
    ok: 'green',
    fixed: 'green',
    not_needed: 'slate',
};

export function Pill({ tone, className, children }: { tone: Tone; className?: string; children: React.ReactNode }) {
    return (
        <span
            className={cn(
                'inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium',
                TONE[tone],
                className,
            )}
        >
            {children}
        </span>
    );
}

export function BoardChipBadge({ chip, late, className }: { chip: BoardChip | null; late?: boolean | null; className?: string }) {
    if (!chip) return <span className="text-xs text-muted-foreground">No round</span>;
    return (
        <span className={cn('inline-flex items-center gap-1.5', className)}>
            <Pill tone={CHIP_TONE[chip]}>{CHIP_LABEL[chip]}</Pill>
            {late && <span className="text-xs font-medium text-orange-700 dark:text-orange-400">late</span>}
        </span>
    );
}

export function ResultBadge({ result, className }: { result: VisitResult | null | undefined; className?: string }) {
    if (!result) return null;
    return (
        <Pill tone={RESULT_TONE[result]} className={className}>
            {RESULT_LABEL[result]}
        </Pill>
    );
}

/** A contract's place in the round, with "late" on a presentation after the window. */
export function AttendanceBadge({ attendance, late, className }: { attendance: Attendance | null | undefined; late?: boolean | null; className?: string }) {
    if (!attendance) return null;
    return (
        <span className={cn('inline-flex items-center gap-1.5', className)}>
            <Pill tone={ATTENDANCE_TONE[attendance]}>{ATTENDANCE_LABEL[attendance]}</Pill>
            {late && <span className="text-xs font-medium text-orange-700 dark:text-orange-400">late</span>}
        </span>
    );
}

export function ConditionBadge({ condition, className }: { condition: Condition | null | undefined; className?: string }) {
    if (!condition) return null;
    return (
        <Pill tone={CONDITION_TONE[condition]} className={className}>
            {CONDITION_LABEL[condition]}
        </Pill>
    );
}

export function GradeBadge({ grade, className }: { grade: Grade; className?: string }) {
    return (
        <Pill tone={GRADE_TONE[grade]} className={className}>
            {GRADE_LABEL[grade]}
        </Pill>
    );
}

/** "2 must fix · 1 fix soon", or a dash when nothing is open. */
export function OpenCounts({ fixNow, fixSoon, className }: { fixNow: number | null | undefined; fixSoon: number | null | undefined; className?: string }) {
    const now = fixNow ?? 0;
    const soon = fixSoon ?? 0;
    if (now === 0 && soon === 0) return <span className={cn('text-muted-foreground', className)}>—</span>;
    return (
        <span className={cn('inline-flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm', className)}>
            {now > 0 && (
                <span className="inline-flex items-center gap-1 font-medium text-red-700 dark:text-red-400">
                    <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-red-500" />
                    {now} must fix
                </span>
            )}
            {soon > 0 && (
                <span className="inline-flex items-center gap-1 text-amber-800 dark:text-amber-400">
                    <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                    {soon} fix soon
                </span>
            )}
        </span>
    );
}
